import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { TrendingDown, TrendingUp, Crown, Briefcase, RefreshCcw, Rocket } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PreviewRibbon } from "@/components/cef/cefShared";

/* Bornwood Stock Exchange — /cef/stocks/:serverId — the city's living market.
   Prices tick with seeded random walks + drift from real city events (robbery
   sprees dent retail, a busy ER floats MEDS). 51% of a float = controlling
   interest: the wire announces the takeover and dividends accrue by the hour. */

const DEMO_LISTINGS = [
  { sym: "FUEL", name: "Harborlight Energy", blurb: "The gas & go network keeping Bornwood moving.", price: 42.18, change_pct: 1.8, float: 10000, controller_pc: null, hist: [41.2, 41.6, 41.1, 41.9, 42.18] },
  { sym: "THRD", name: "Thread & Timber", blurb: "Workwear and streetwear for the north side.", price: 17.84, change_pct: -2.4, float: 10000, controller_pc: null, hist: [18.6, 18.3, 18.5, 18.0, 17.84] },
  { sym: "AURM", name: "Aurum & Ash", blurb: "Fine gems for people who don't ask questions.", price: 263.4, change_pct: 0.6, float: 4000, controller_pc: null, hist: [258, 260, 261.8, 259.5, 263.4] },
  { sym: "LUCK", name: "Golden Mare Casino", blurb: "The house always wins — until it doesn't.", price: 91.25, change_pct: 3.7, float: 8000, controller_pc: null, hist: [86, 87.4, 88.9, 90.2, 91.25] },
  { sym: "BNKB", name: "First Bornwood Bank", blurb: "Every dollar in this city sleeps here.", price: 119.6, change_pct: -0.4, float: 12000, controller_pc: null, hist: [120.2, 120, 119.8, 120.1, 119.6] },
  { sym: "GPAK", name: "Ghost-Pak Logistics", blurb: "No questions. Just delivery.", price: 32.15, change_pct: 2.2, float: 6000, controller_pc: 37049, hist: [30.8, 31.1, 31.4, 31.9, 32.15] },
];

function Spark({ hist, up }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c || !hist?.length) return;
    const ctx = c.getContext("2d");
    const W = c.width = c.clientWidth, H = c.height = c.clientHeight;
    const min = Math.min(...hist), max = Math.max(...hist);
    const span = max - min || 1;
    ctx.strokeStyle = up ? "#4ade80" : "#f87171";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    hist.forEach((p, i) => ctx.lineTo((i / (hist.length - 1)) * W, H - 3 - ((p - min) / span) * (H - 6)));
    ctx.stroke();
  }, [hist, up]);
  return <canvas ref={ref} className="w-20 h-6" />;
}

export default function StocksCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;

  const [listings, setListings] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [netWorth, setNetWorth] = useState(0);
  const [shares, setShares] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    if (demo) { setListings(DEMO_LISTINGS); setPortfolio([{ sym: "GPAK", name: "Ghost-Pak Logistics", shares: 3200, avg_cost: 28.1, price: 32.15, value: 102880, pnl: 12960, controlling: true }]); setNetWorth(102880); return; }
    try {
      const r = await api.get(`/nc/servers/${serverId}/stocks`, { params: { token } });
      setListings(r.data.listings);
      const p = await api.get(`/nc/servers/${serverId}/stocks/portfolio/me`, { params: { token } });
      setPortfolio(p.data.portfolio);
      setNetWorth(p.data.net_worth);
    } catch (e) { toast.error(e.response?.data?.detail || "Tape unavailable"); }
  }, [serverId, token, demo]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const trade = async (sym, side) => {
    const n = parseInt(shares[sym] || "0", 10);
    if (!n || n < 1) { toast.error("Enter a share count first"); return; }
    setBusy(sym + side);
    try {
      if (demo) { toast.success(`${side === "buy" ? "Bought" : "Sold"} ${n} ${sym} (demo)`); return; }
      const r = await api.post(`/nc/servers/${serverId}/stocks/${sym}/${side}`, { token, shares: n });
      toast.success(r.data.message);
      if (r.data.takeover) toast.success("CONTROLLING INTEREST SEIZED — the wire knows your name");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Trade rejected"); }
    finally { setBusy(""); }
  };

  const dividends = async (sym) => {
    try {
      if (demo) { toast.success("Dividends claimed (demo)"); return; }
      const r = await api.post(`/nc/servers/${serverId}/stocks/${sym}/dividends`, { token, shares: 1 });
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Nothing to claim"); }
  };

  /* player IPOs — citizens float their own operation on the tape */
  const [ipos, setIpos] = useState([]);
  const [ipoHoldings, setIpoHoldings] = useState([]);
  const [ipoQty, setIpoQty] = useState({});
  const [listForm, setListForm] = useState({ name: "", ticker: "" });
  const [showList, setShowList] = useState(false);

  const loadIpos = useCallback(async () => {
    if (demo) {
      setIpos([{ id: "ipo1", name: "Downtown Cab Co", ticker: "CAB", price: 142.5, shares_out: 400, owner_name: "Ray Dalton", hist: [100, 110, 118, 131, 142.5] }]);
      setIpoHoldings([{ ipo_id: "ipo1", ticker: "CAB", qty: 10 }]);
      return;
    }
    try {
      const r = await api.get(`/nc/servers/${serverId}/ipo/board`, { params: { token } });
      setIpos(r.data.ipos); setIpoHoldings(r.data.my_holdings);
    } catch { /* board optional */ }
  }, [serverId, token, demo]);
  useEffect(() => { loadIpos(); }, [loadIpos]);

  const ipoTrade = async (ipo, side) => {
    const n = parseInt(ipoQty[ipo.id] || "0", 10);
    if (!n || n < 1) { toast.error("Enter a share count first"); return; }
    setBusy(ipo.id + side);
    try {
      if (demo) { toast.success(`${side === "buy" ? "Bought" : "Sold"} ${n} $${ipo.ticker} (demo)`); return; }
      const r = await api.post(`/nc/servers/${serverId}/ipo/${ipo.id}/${side}`, { token, shares: n });
      toast.success(r.data.message);
      loadIpos();
    } catch (e) { toast.error(e.response?.data?.detail || "Trade rejected"); }
    finally { setBusy(""); }
  };

  const listBusiness = async () => {
    if (busy === "list") return;
    setBusy("list");
    try {
      if (demo) { toast.success("Preview: your IPO hits the tape at $100 (demo)"); return; }
      const r = await api.post(`/nc/servers/${serverId}/ipo/list`, { token, name: listForm.name, ticker: listForm.ticker.toUpperCase() });
      toast.success(r.data.message);
      setListForm({ name: "", ticker: "" }); setShowList(false);
      loadIpos();
    } catch (e) { toast.error(e.response?.data?.detail || "Listing rejected"); }
    finally { setBusy(""); }
  };

  const held = Object.fromEntries(portfolio.map((p) => [p.sym, p]));

  return (
    <div data-testid="stocks-page" className="min-h-screen bg-transparent flex items-center justify-center p-4 relative select-none">
      {demo && <PreviewRibbon what="the exchange" />}
      <div className="w-full max-w-3xl border border-white/12 bg-black/90 backdrop-blur-md" style={{ animation: "ncFadeUp 0.3s ease both" }}>
        <style>{`@keyframes ncFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }`}</style>
        <header className="flex items-center gap-3 px-5 py-3.5 border-b border-white/10">
          <TrendingUp className="w-5 h-5 text-emerald-400" />
          <div>
            <h1 className="font-sans font-bold uppercase tracking-[0.25em] text-sm text-zinc-100">Bornwood Stock Exchange</h1>
            <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.2em]">the tape follows the streets · 51% = control</p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span data-testid="stocks-networth" className="font-mono text-[10px] text-emerald-300 flex items-center gap-1.5"><Briefcase className="w-3 h-3" /> ${netWorth.toLocaleString()}</span>
            <button data-testid="stocks-refresh" onClick={load} className="text-zinc-500 hover:text-zinc-200 transition-colors"><RefreshCcw className="w-3.5 h-3.5" /></button>
          </div>
        </header>

        <div className="divide-y divide-white/5 max-h-[54vh] overflow-y-auto">
          {listings.map((l) => {
            const up = l.change_pct >= 0;
            const h = held[l.sym];
            return (
              <div key={l.sym} data-testid={`stock-${l.sym}`} className="flex items-center gap-4 px-5 py-3 flex-wrap">
                <div className="w-28">
                  <p className="font-mono font-bold text-[12px] text-zinc-100 flex items-center gap-1.5">
                    {l.sym}
                    {(h?.controlling || l.controller_pc) && <Crown className="w-3 h-3 text-amber-400" />}
                  </p>
                  <p className="font-mono text-[8px] text-zinc-500 truncate">{l.name}</p>
                </div>
                <Spark hist={l.hist} up={up} />
                <div className="text-right w-24">
                  <p className="font-mono text-[12px] text-zinc-100">${l.price.toFixed(2)}</p>
                  <p data-testid={`stock-change-${l.sym}`} className={`font-mono text-[9px] flex items-center justify-end gap-1 ${up ? "text-emerald-400" : "text-red-400"}`}>
                    {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{up ? "+" : ""}{l.change_pct}%
                  </p>
                </div>
                {h && (
                  <p className="font-mono text-[9px] text-zinc-500 w-32">
                    {h.shares} sh · <span className={h.pnl >= 0 ? "text-emerald-400" : "text-red-400"}>{h.pnl >= 0 ? "+" : ""}${h.pnl.toLocaleString()}</span>
                  </p>
                )}
                <div className="ml-auto flex items-center gap-1.5">
                  <input data-testid={`stock-qty-${l.sym}`} value={shares[l.sym] || ""} onChange={(e) => setShares((s) => ({ ...s, [l.sym]: e.target.value.replace(/\D/g, "") }))}
                    placeholder="qty" className="w-16 bg-black border border-white/15 font-mono text-[10px] text-zinc-100 px-2 py-1.5 [color-scheme:dark]" />
                  <button data-testid={`stock-buy-${l.sym}`} onClick={() => trade(l.sym, "buy")} disabled={!!busy}
                    className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black font-mono text-[9px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 transition-colors">Buy</button>
                  <button data-testid={`stock-sell-${l.sym}`} onClick={() => trade(l.sym, "sell")} disabled={!!busy || !h}
                    className="border border-red-400/40 text-red-400 hover:bg-red-400/10 disabled:opacity-30 font-mono text-[9px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 transition-colors">Sell</button>
                  {h?.controlling && (
                    <button data-testid={`stock-dividends-${l.sym}`} onClick={() => dividends(l.sym)}
                      className="border border-amber-400/50 text-amber-300 hover:bg-amber-400/10 font-mono text-[9px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 flex items-center gap-1 transition-colors">
                      <Crown className="w-3 h-3" /> Dividends
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <p className="px-5 py-2.5 border-t border-white/10 font-mono text-[8px] text-zinc-600 uppercase tracking-[0.2em]">
          Fictional exchange — in-game currency only, no real-world value · tape refreshes every 30s
        </p>

        {/* citizen IPO board */}
        <div className="border-t border-white/10">
          <div className="flex items-center gap-3 px-5 py-3">
            <Rocket className="w-4 h-4 text-sky-400" />
            <p className="font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-zinc-300">Citizen IPOs</p>
            <span className="font-mono text-[8px] text-zinc-600 uppercase tracking-[0.15em]">operators are their own market makers</span>
            <button data-testid="ipo-list-open" onClick={() => setShowList((s) => !s)}
              className="ml-auto font-mono text-[8px] uppercase tracking-[0.15em] border border-sky-400/40 text-sky-300 hover:bg-sky-400/10 px-3 py-1.5 transition-colors">
              Float your business — $5,000
            </button>
          </div>
          {showList && (
            <div className="flex items-center gap-2 px-5 pb-3 flex-wrap">
              <input data-testid="ipo-name" value={listForm.name} maxLength={40} onChange={(e) => setListForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Business name" className="w-48 bg-black border border-white/15 font-mono text-[10px] text-zinc-100 px-2.5 py-2 [color-scheme:dark]" />
              <input data-testid="ipo-ticker" value={listForm.ticker} maxLength={5} onChange={(e) => setListForm((f) => ({ ...f, ticker: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") }))}
                placeholder="TICKER" className="w-24 bg-black border border-white/15 font-mono text-[10px] text-zinc-100 px-2.5 py-2 [color-scheme:dark]" />
              <button data-testid="ipo-list-submit" onClick={listBusiness} disabled={busy === "list" || listForm.name.trim().length < 3 || listForm.ticker.length < 2}
                className="bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-black font-mono text-[9px] font-bold uppercase tracking-[0.15em] px-4 py-2 transition-colors">
                Ring the bell
              </button>
            </div>
          )}
          {ipos.length === 0 && <p className="px-5 pb-4 font-mono text-[9px] text-zinc-600">No citizen listings yet — the first operator to float gets all the attention.</p>}
          <div className="divide-y divide-white/5 max-h-[30vh] overflow-y-auto">
            {ipos.map((i) => {
              const h = ipoHoldings.find((x) => x.ipo_id === i.id);
              const chg = i.hist?.length > 1 ? Math.round((i.price / i.hist[0] - 1) * 10000) / 100 : 0;
              return (
                <div key={i.id} data-testid={`ipo-${i.ticker}`} className="flex items-center gap-4 px-5 py-3 flex-wrap">
                  <div className="w-28">
                    <p className="font-mono font-bold text-[12px] text-sky-300">${i.ticker}</p>
                    <p className="font-mono text-[8px] text-zinc-500 truncate">{i.name} · {i.owner_name}</p>
                  </div>
                  <Spark hist={i.hist || []} up={chg >= 0} />
                  <div className="text-right w-24">
                    <p className="font-mono text-[12px] text-zinc-100">${i.price.toFixed(2)}</p>
                    <p className={`font-mono text-[9px] ${chg >= 0 ? "text-emerald-400" : "text-red-400"}`}>{chg >= 0 ? "+" : ""}{chg}%</p>
                  </div>
                  <p className="font-mono text-[9px] text-zinc-500 w-32">{i.shares_out} out{h ? ` · you hold ${h.qty}` : ""}</p>
                  <div className="ml-auto flex items-center gap-1.5">
                    <input data-testid={`ipo-qty-${i.ticker}`} value={ipoQty[i.id] || ""} onChange={(e) => setIpoQty((s) => ({ ...s, [i.id]: e.target.value.replace(/\D/g, "") }))}
                      placeholder="qty" className="w-16 bg-black border border-white/15 font-mono text-[10px] text-zinc-100 px-2 py-1.5 [color-scheme:dark]" />
                    <button data-testid={`ipo-buy-${i.ticker}`} onClick={() => ipoTrade(i, "buy")} disabled={!!busy}
                      className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black font-mono text-[9px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 transition-colors">Buy</button>
                    <button data-testid={`ipo-sell-${i.ticker}`} onClick={() => ipoTrade(i, "sell")} disabled={!!busy || !h}
                      className="border border-red-400/40 text-red-400 hover:bg-red-400/10 disabled:opacity-30 font-mono text-[9px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 transition-colors">Sell</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
